<template>
  <div class="llm-page">
    <section class="page-card">
      <header class="page-header">
        <div>
          <h1>大语言模型管理</h1>
          <p>管理模型平台、API 地址、访问令牌、模型版本及思考强度</p>
        </div>
        <div class="header-actions">
          <el-button :icon="Refresh" :loading="loading" @click="fetchList">刷新</el-button>
          <el-button type="primary" :icon="Plus" @click="openCreate">新增模型平台</el-button>
        </div>
      </header>

      <div class="notice-bar">
        <span class="notice-icon">i</span>
        <span>每个平台使用独立的 API 地址和 Token；Token 列表不返回明文，管理员打开编辑窗口时可查看和修改。</span>
      </div>

      <div class="filter-row">
        <el-input
          v-model="keyword"
          clearable
          placeholder="搜索平台名称"
          @keyup.enter="fetchList"
          @clear="fetchList"
        />
        <el-button type="primary" @click="fetchList">查询</el-button>
      </div>

      <div class="table-shell">
        <el-table
          v-loading="loading"
          :data="platformList"
          row-key="id"
          class="platform-table"
          empty-text="暂无大语言模型平台"
        >
          <el-table-column type="expand" width="54">
            <template #default="scope">
              <div class="version-expand">
                <div class="version-expand__title">
                  <span>模型版本</span>
                  <small>价格单位：元 / 百万 Token</small>
                </div>
                <el-table :data="scope.row.versions || []" size="small" class="version-table">
                  <el-table-column prop="display_name" label="显示名称" min-width="140" />
                  <el-table-column prop="model_key" label="版本标识" min-width="180" />
                  <el-table-column label="思考强度" min-width="170">
                    <template #default="versionScope">
                      <el-tag
                        v-for="level in versionScope.row.reasoning_levels || []"
                        :key="level"
                        size="small"
                        class="reasoning-tag"
                      >{{ reasoningLabel(level) }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column prop="max_output_tokens" label="最大输出 Token" width="140" />
                  <el-table-column label="输入价格" width="120">
                    <template #default="versionScope">{{ money(versionScope.row.input_price) }}</template>
                  </el-table-column>
                  <el-table-column label="输出价格" width="120">
                    <template #default="versionScope">{{ money(versionScope.row.output_price) }}</template>
                  </el-table-column>
                  <el-table-column label="状态" width="90">
                    <template #default="versionScope">
                      <el-tag :type="Number(versionScope.row.status) === 1 ? 'success' : 'info'" size="small">
                        {{ Number(versionScope.row.status) === 1 ? '启用' : '停用' }}
                      </el-tag>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="sort_order" label="排序" width="86" align="center" />
          <el-table-column label="模型平台" min-width="180">
            <template #default="scope">
              <div class="platform-name">
                <img v-if="scope.row.icon_path" class="platform-logo platform-logo--image" :src="iconUrl(scope.row.icon_path)" alt="" />
                <span v-else class="platform-logo" :class="logoClass(scope.row.platform_name)">
                  {{ platformInitial(scope.row.platform_name) }}
                </span>
                <strong>{{ scope.row.platform_name }}</strong>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="api_url" label="API 地址" min-width="310" show-overflow-tooltip />
          <el-table-column label="Token" width="125" align="center">
            <template #default="scope">
              <el-tag v-if="Number(scope.row.token_configured) === 1" type="success" effect="dark" class="token-tag">
                <el-icon><Lock /></el-icon>&nbsp;已配置
              </el-tag>
              <el-tag v-else type="danger" effect="plain">未配置</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="version_count" label="版本数" width="90" align="center" />
          <el-table-column label="状态" width="100" align="center">
            <template #default="scope">
              <el-switch
                v-model="scope.row.status"
                :active-value="1"
                :inactive-value="0"
                :loading="scope.row.statusLoading"
                @change="value => updateStatus(scope.row, value)"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="286" fixed="right">
            <template #default="scope">
              <el-button class="version-button" size="small" @click="openAddVersion(scope.row)">
                <el-icon><Plus /></el-icon>&nbsp;新增版本
              </el-button>
              <el-button type="primary" plain size="small" :icon="Edit" @click="openEdit(scope.row)">编辑</el-button>
              <el-button type="danger" plain size="small" :icon="Delete" @click="removePlatform(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </section>

    <el-dialog
      v-model="dialogVisible"
      :title="dialogMode === 'add' ? '新增模型平台' : '编辑模型平台'"
      width="min(1500px, 94vw)"
      class="llm-dialog"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <div class="dialog-layout">
        <div class="dialog-content">
          <section class="form-section">
            <h2>基础信息</h2>
            <el-form label-position="top">
              <el-row :gutter="24">
                <el-col :xs="24" :md="12">
                  <el-form-item label="平台名称 *">
                    <el-input v-model="formData.platform.platform_name" maxlength="64" placeholder="例如：GPT" />
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="12">
                  <el-form-item label="平台图标">
                    <div class="icon-upload-row">
                      <img
                        v-if="formData.platform.icon_path"
                        class="icon-preview"
                        :src="iconUrl(formData.platform.icon_path)"
                        alt="平台图标"
                      />
                      <span v-else class="icon-preview icon-preview--empty">暂无</span>
                      <el-upload
                        :show-file-list="false"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        :http-request="uploadPlatformIcon"
                        :disabled="iconUploading"
                      >
                        <el-button :loading="iconUploading" type="primary" plain>
                          {{ formData.platform.icon_path ? '更换图标' : '上传图标' }}
                        </el-button>
                      </el-upload>
                      <el-button v-if="formData.platform.icon_path" link type="danger" @click="formData.platform.icon_path = ''">
                        移除
                      </el-button>
                      <small>PNG、JPG、WEBP 或 GIF，不超过 2MB</small>
                    </div>
                  </el-form-item>
                </el-col>
                <el-col :span="24">
                  <el-form-item label="API 地址 *">
                    <el-input v-model="formData.platform.api_url" maxlength="500" placeholder="https://example.com/v1/chat/completions" />
                  </el-form-item>
                </el-col>
                <el-col :span="24">
                  <el-form-item label="API Token">
                    <el-input
                      v-model="formData.platform.api_token"
                      :type="tokenVisible ? 'text' : 'password'"
                      maxlength="1000"
                      autocomplete="new-password"
                      placeholder="请输入平台 Token"
                    >
                      <template #suffix>
                        <el-icon class="token-eye" @click="tokenVisible = !tokenVisible">
                          <View v-if="!tokenVisible" />
                          <Hide v-else />
                        </el-icon>
                      </template>
                    </el-input>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="12">
                  <el-form-item label="排序">
                    <el-input-number v-model="formData.platform.sort_order" :min="0" :max="999999" controls-position="right" />
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="12">
                  <el-form-item label="启用状态">
                    <div class="status-control">
                      <el-switch v-model="formData.platform.status" :active-value="1" :inactive-value="0" />
                      <span>{{ Number(formData.platform.status) === 1 ? '已启用' : '已停用' }}</span>
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </section>

          <section ref="versionsSectionRef" class="form-section versions-section">
            <div class="section-title-row">
              <div>
                <h2>模型版本</h2>
                <p>每个版本分别设置思考强度、Token 限制和对外售价</p>
              </div>
              <el-button type="primary" :icon="Plus" @click="addVersion">新增版本</el-button>
            </div>

            <div v-if="!formData.versions.length" class="empty-versions">暂无版本，请点击“新增版本”</div>

            <article v-for="(version, index) in formData.versions" :key="version.localKey" class="version-card">
              <header>
                <strong>版本 {{ index + 1 }}</strong>
                <el-button type="danger" link :icon="Delete" @click="removeVersion(index)">删除</el-button>
              </header>

              <el-row :gutter="20">
                <el-col :xs="24" :md="8">
                  <el-form-item label="显示名称 *">
                    <el-input v-model="version.display_name" maxlength="100" placeholder="例如：gpt-4.1" />
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="8">
                  <el-form-item label="版本标识 *">
                    <el-input v-model="version.model_key" maxlength="128" placeholder="上游 model 参数" />
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="8">
                  <el-form-item label="支持的思考强度 *">
                    <el-select v-model="version.reasoning_levels" multiple placeholder="请选择">
                      <el-option v-for="option in reasoningOptions" :key="option.value" :label="option.label" :value="option.value" />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="6">
                  <el-form-item label="默认思考强度 *">
                    <el-select v-model="version.default_reasoning_level" placeholder="请选择">
                      <el-option
                        v-for="level in version.reasoning_levels"
                        :key="level"
                        :label="reasoningLabel(level)"
                        :value="level"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="6">
                  <el-form-item label="最大输出 Token">
                    <el-input-number v-model="version.max_output_tokens" :min="0" :max="10000000" controls-position="right" />
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="6">
                  <el-form-item label="输入价格 *">
                    <el-input-number v-model="version.input_price" :min="0" :precision="4" :step="0.1" controls-position="right" />
                    <span class="unit-text">元/百万 Token</span>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="6">
                  <el-form-item label="输出价格 *">
                    <el-input-number v-model="version.output_price" :min="0" :precision="4" :step="0.1" controls-position="right" />
                    <span class="unit-text">元/百万 Token</span>
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="6">
                  <el-form-item label="排序">
                    <el-input-number v-model="version.sort_order" :min="0" :max="999999" controls-position="right" />
                  </el-form-item>
                </el-col>
                <el-col :xs="24" :md="6">
                  <el-form-item label="状态">
                    <div class="status-control">
                      <el-switch v-model="version.status" :active-value="1" :inactive-value="0" />
                      <span>{{ Number(version.status) === 1 ? '启用' : '停用' }}</span>
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>
            </article>
          </section>
        </div>
      </div>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="savePlatform">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { nextTick, onMounted, reactive, ref } from "vue";
import axios from "axios";
import { ElMessage, ElMessageBox } from "element-plus";
import { Delete, Edit, Hide, Lock, Plus, Refresh, View } from "@element-plus/icons-vue";
import { API_URL } from "@/util/urls";

const loading = ref(false);
const saving = ref(false);
const keyword = ref("");
const platformList = ref([]);
const dialogVisible = ref(false);
const dialogMode = ref("add");
const tokenVisible = ref(false);
const iconUploading = ref(false);
const versionsSectionRef = ref(null);

const reasoningOptions = [
  { label: "低", value: "low" },
  { label: "中", value: "medium" },
  { label: "高", value: "high" }
];

let localVersionId = 0;

const createPlatform = () => ({
  id: null,
  platform_name: "",
  icon_path: "",
  api_url: "",
  api_token: "",
  sort_order: 0,
  status: 1
});

const createVersion = (sortOrder = 0) => ({
  id: null,
  localKey: `new-${++localVersionId}`,
  display_name: "",
  model_key: "",
  reasoning_levels: ["medium"],
  default_reasoning_level: "medium",
  max_output_tokens: 0,
  input_price: 0,
  output_price: 0,
  sort_order: sortOrder,
  status: 1
});

const formData = reactive({
  platform: createPlatform(),
  versions: []
});

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;

const parseReasoningLevels = value => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return String(value).split(",").map(item => item.trim()).filter(Boolean);
  }
};

const normalizeVersion = (item, index) => ({
  ...createVersion(index),
  ...item,
  localKey: item.id ? `id-${item.id}` : `new-${++localVersionId}`,
  reasoning_levels: parseReasoningLevels(item.reasoning_levels),
  max_output_tokens: Number(item.max_output_tokens || 0),
  input_price: Number(item.input_price || 0),
  output_price: Number(item.output_price || 0),
  sort_order: Number(item.sort_order || 0),
  status: Number(item.status ?? 1)
});

const reasoningLabel = value => reasoningOptions.find(item => item.value === value)?.label || value;
const money = value => Number(value || 0).toFixed(4);
const platformInitial = name => String(name || "AI").trim().slice(0, 2).toUpperCase();
const logoClass = key => `logo-${String(key || "default").charCodeAt(0) % 4}`;
const iconUrl = path => {
  const value = String(path || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/getfile/")) return `${API_URL}${value}`;
  return `${API_URL}/getfile/${value.replace(/^\/+/, "")}`;
};

const uploadPlatformIcon = async options => {
  const file = options.file;
  if (!file) return;
  if (!/^image\/(png|jpeg|webp|gif)$/i.test(file.type || "")) {
    ElMessage.warning("只允许上传 PNG、JPG、WEBP 或 GIF 图片");
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    ElMessage.warning("图标文件不能超过 2MB");
    return;
  }

  iconUploading.value = true;
  try {
    const uploadData = new FormData();
    uploadData.append("icon", file);
    const response = await axios.post("/admin/uploadLlmPlatformIcon", uploadData);
    const path = response.data?.data?.path;
    if (!path) throw new Error("上传接口未返回图片路径");
    // 数据库只保存相对路径，展示时再拼接 /getfile。
    formData.platform.icon_path = path;
    ElMessage.success("图标上传成功");
  } catch (error) {
    ElMessage.error(errorMessage(error, "图标上传失败"));
  } finally {
    iconUploading.value = false;
  }
};

const fetchList = async () => {
  loading.value = true;
  try {
    const response = await axios.get("/admin/getLlmPlatformList", {
      params: { keyword: keyword.value.trim() }
    });
    const data = response.data?.data || {};
    platformList.value = (data.list || []).map(item => ({
      ...item,
      status: Number(item.status),
      version_count: Number(item.version_count || 0),
      token_configured: Number(item.token_configured || 0),
      versions: (item.versions || []).map(normalizeVersion),
      statusLoading: false
    }));
  } catch (error) {
    ElMessage.error(errorMessage(error, "获取大语言模型列表失败"));
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  Object.assign(formData.platform, createPlatform());
  formData.versions = [];
  tokenVisible.value = false;
};

const openCreate = () => {
  resetForm();
  dialogMode.value = "add";
  formData.versions.push(createVersion(0));
  dialogVisible.value = true;
};

const loadDetail = async row => {
  const response = await axios.get("/admin/getLlmPlatformDetail", {
    params: { id: row.id }
  });
  const data = response.data?.data || {};
  Object.assign(formData.platform, createPlatform(), data.platform || {});
  formData.platform.id = Number(row.id);
  formData.platform.sort_order = Number(formData.platform.sort_order || 0);
  formData.platform.status = Number(formData.platform.status ?? 1);
  formData.versions = (data.versions || []).map(normalizeVersion);
};

const openEdit = async row => {
  resetForm();
  dialogMode.value = "edit";
  try {
    await loadDetail(row);
    dialogVisible.value = true;
  } catch (error) {
    ElMessage.error(errorMessage(error, "获取平台详情失败"));
  }
};

const openAddVersion = async row => {
  await openEdit(row);
  if (!dialogVisible.value) return;
  addVersion();
  await nextTick();
  versionsSectionRef.value?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const addVersion = () => {
  formData.versions.push(createVersion(formData.versions.length));
};

const removeVersion = index => {
  formData.versions.splice(index, 1);
};

const validateForm = () => {
  const platform = formData.platform;
  if (!String(platform.platform_name || "").trim()) return "请输入平台名称";
  try {
    const url = new URL(String(platform.api_url || "").trim());
    if (!["http:", "https:"].includes(url.protocol)) return "API 地址必须是 HTTP 或 HTTPS";
  } catch (error) {
    return "请输入正确的 API 地址";
  }
  for (let index = 0; index < formData.versions.length; index++) {
    const version = formData.versions[index];
    if (!String(version.display_name || "").trim() || !String(version.model_key || "").trim()) {
      return `第 ${index + 1} 个版本的显示名称和版本标识必填`;
    }
    if (!version.reasoning_levels.length) return `第 ${index + 1} 个版本请至少选择一个思考强度`;
    if (!version.reasoning_levels.includes(version.default_reasoning_level)) {
      return `第 ${index + 1} 个版本的默认思考强度必须在支持列表中`;
    }
    if (Number(version.input_price) < 0 || Number(version.output_price) < 0) return `第 ${index + 1} 个版本的价格不能小于 0`;
  }
  return "";
};

const savePlatform = async () => {
  const validationError = validateForm();
  if (validationError) {
    ElMessage.warning(validationError);
    return;
  }

  saving.value = true;
  try {
    const payload = {
      platform: {
        ...formData.platform,
        platform_name: formData.platform.platform_name.trim(),
        icon_path: String(formData.platform.icon_path || "").trim(),
        api_url: formData.platform.api_url.trim(),
        api_token: formData.platform.api_token.trim(),
        sort_order: Number(formData.platform.sort_order || 0),
        status: Number(formData.platform.status)
      },
      versions: formData.versions.map((item, index) => ({
        id: item.id || undefined,
        display_name: String(item.display_name || "").trim(),
        model_key: String(item.model_key || "").trim(),
        reasoning_levels: item.reasoning_levels,
        default_reasoning_level: item.default_reasoning_level,
        max_output_tokens: Number(item.max_output_tokens || 0),
        input_price: Number(item.input_price || 0),
        output_price: Number(item.output_price || 0),
        sort_order: Number(item.sort_order ?? index),
        status: Number(item.status)
      }))
    };
    await axios.post("/admin/saveLlmPlatform", payload);
    ElMessage.success("保存成功");
    dialogVisible.value = false;
    await fetchList();
  } catch (error) {
    ElMessage.error(errorMessage(error, "保存失败"));
  } finally {
    saving.value = false;
  }
};

const updateStatus = async (row, value) => {
  const oldValue = Number(value) === 1 ? 0 : 1;
  row.statusLoading = true;
  try {
    await axios.post("/admin/updateLlmPlatformStatus", { id: row.id, status: Number(value) });
    ElMessage.success("状态已更新");
  } catch (error) {
    row.status = oldValue;
    ElMessage.error(errorMessage(error, "状态更新失败"));
  } finally {
    row.statusLoading = false;
  }
};

const removePlatform = async row => {
  try {
    await ElMessageBox.confirm(
      `确定删除“${row.platform_name}”及其全部版本吗？`,
      "删除确认",
      { type: "warning", confirmButtonText: "删除", cancelButtonText: "取消" }
    );
    await axios.post("/admin/deleteLlmPlatform", { id: row.id });
    ElMessage.success("删除成功");
    await fetchList();
  } catch (error) {
    if (error === "cancel" || error === "close") return;
    ElMessage.error(errorMessage(error, "删除失败"));
  }
};

onMounted(fetchList);
</script>

<style scoped lang="scss">
.llm-page {
  min-height: 100%;
  padding: 26px;
  box-sizing: border-box;
  color: #e9f0fb;
  background: #07101d;
}

.page-card {
  min-height: calc(100vh - 112px);
  padding: 28px 30px;
  box-sizing: border-box;
  background: radial-gradient(circle at 60% 0, rgb(35 65 109 / 18%), transparent 40%), #0d1726;
  border: 1px solid #26364d;
  border-radius: 16px;
  box-shadow: 0 18px 50px rgb(0 0 0 / 20%);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 24px;
  border-bottom: 1px solid #253348;

  h1 {
    margin: 0 0 12px;
    color: #f5f8ff;
    font-size: 26px;
  }

  p {
    margin: 0;
    color: #74849b;
    font-size: 14px;
  }
}

.header-actions {
  display: flex;
  gap: 10px;
}

.notice-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 22px 0 16px;
  padding: 16px 18px;
  color: #9eabc0;
  background: rgb(34 78 145 / 18%);
  border: 1px solid #254a7d;
  border-radius: 10px;
}

.notice-icon {
  display: inline-grid;
  width: 22px;
  height: 22px;
  place-items: center;
  flex: 0 0 auto;
  color: white;
  background: #338bf2;
  border-radius: 50%;
  font-weight: 700;
}

.filter-row {
  display: flex;
  width: 410px;
  gap: 10px;
  margin-bottom: 16px;
}

.table-shell {
  overflow: hidden;
  border: 1px solid #2b3a50;
  border-radius: 12px;
}

:deep(.platform-table),
:deep(.version-table) {
  --el-table-bg-color: #101a29;
  --el-table-tr-bg-color: #101a29;
  --el-table-header-bg-color: #172233;
  --el-table-row-hover-bg-color: #17263a;
  --el-table-border-color: #26364b;
  --el-table-text-color: #bac6d8;
  --el-table-header-text-color: #aebbd0;
}

:deep(.platform-table th.el-table__cell) {
  height: 58px;
  font-weight: 600;
}

:deep(.platform-table td.el-table__cell) {
  height: 70px;
}

.platform-name {
  display: flex;
  align-items: center;
  gap: 12px;

  strong { color: #eef4ff; }
}

.platform-logo {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  color: #dff8ec;
  background: linear-gradient(145deg, #138e67, #31c38e);
  border-radius: 11px;
  font-size: 13px;
  font-weight: 800;
}

.platform-logo--image {
  display: block;
  object-fit: cover;
  background: #111c2b;
}

.logo-1 { background: linear-gradient(145deg, #5c6cff, #9668ff); }
.logo-2 { background: linear-gradient(145deg, #ff8a5b, #ffb16b); }
.logo-3 { background: linear-gradient(145deg, #1678d6, #39a4ff); }

.token-tag {
  background: #123526;
  border-color: #245a42;
  color: #78d69f;
}

.version-button {
  color: #71ce92;
  background: #122a20;
  border-color: #285d3d;
}

.version-expand {
  padding: 18px 28px 24px 72px;
  background: #0a1422;
}

.version-expand__title {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 12px;
  color: #e7effc;
  font-weight: 600;

  small { color: #728198; font-weight: 400; }
}

.reasoning-tag { margin-right: 5px; }

:deep(.llm-dialog) {
  --el-bg-color: #0a1421;
  --el-bg-color-overlay: #0a1421;
  --el-text-color-primary: #e8effa;
  --el-text-color-regular: #aebbd0;
  --el-border-color: #29384c;
  margin-top: 3vh !important;
  background: #08121f !important;
  border: 1px solid #25354a !important;
  border-radius: 14px !important;
}

:global(.llm-dialog .el-dialog__header) {
  padding: 22px 26px;
  margin: 0;
  border-bottom: 1px solid #243247 !important;
}

:global(.llm-dialog .el-dialog__title) {
  color: #f3f7fe !important;
  font-size: 22px !important;
  font-weight: 700 !important;
}

:global(.llm-dialog .el-dialog__body) {
  padding: 0 !important;
  background: #08121f !important;
}

:global(.llm-dialog .el-dialog__footer) {
  padding: 16px 26px !important;
  background: #08121f !important;
  border-top: 1px solid #243247 !important;
}

.dialog-layout {
  height: 76vh;
  min-height: 540px;
}

.dialog-content {
  height: 100%;
  padding: 24px;
  box-sizing: border-box;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.form-section {
  margin-bottom: 20px;
  padding: 22px 24px 8px;
  scroll-margin-top: 20px;
  background: linear-gradient(145deg, #101c2b, #0d1825);
  border: 1px solid #29394f;
  border-radius: 10px;

  h2 {
    margin: 0 0 20px;
    color: #dbe6f7;
    font-size: 17px;
  }
}

.versions-section { padding-bottom: 22px; }

.section-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 18px;

  h2 { margin-bottom: 6px; }
  p { margin: 0; color: #6f8099; font-size: 13px; }
}

.version-card {
  margin-top: 14px;
  padding: 18px 18px 2px;
  background: #0d1724;
  border: 1px solid #2a394d;
  border-radius: 9px;

  > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    color: #dce6f5;
  }
}

.empty-versions {
  padding: 36px;
  color: #6f8098;
  border: 1px dashed #304058;
  border-radius: 8px;
  text-align: center;
}

.status-control {
  display: flex;
  height: 32px;
  align-items: center;
  gap: 12px;
  color: #5299ff;
}

.unit-text {
  margin-left: 8px;
  color: #75849a;
  font-size: 12px;
  white-space: nowrap;
}

.token-eye { cursor: pointer; }

.icon-upload-row {
  display: flex;
  min-height: 56px;
  align-items: center;
  gap: 12px;

  small {
    color: #718198;
    font-size: 12px;
  }
}

.icon-preview {
  display: block;
  width: 52px;
  height: 52px;
  flex: 0 0 auto;
  object-fit: cover;
  background: #0b1522;
  border: 1px solid #304158;
  border-radius: 10px;
}

.icon-preview--empty {
  display: grid;
  place-items: center;
  color: #65758d;
  font-size: 12px;
}

:deep(.el-form-item__label) { color: #9aa9bf; }
:deep(.el-input__wrapper),
:deep(.el-select__wrapper) {
  background: #0e1927 !important;
  box-shadow: 0 0 0 1px #2a3a50 inset !important;
}
:deep(.el-input__inner) { color: #dce6f5; }
:deep(.el-input-number) { width: 100%; }

@media (max-width: 900px) {
  .llm-page { padding: 12px; }
  .page-card { padding: 18px; }
  .page-header { align-items: flex-start; flex-direction: column; }
  .filter-row { width: 100%; }
}
</style>
